import React, { useMemo, useState } from 'react';
import { sortBySearchPriority } from '../../utils/searchPriority';

/**
 * Pick the menu item a bill line really was. Server suggestions first (chips),
 * then a ranked search. Banquet (_BQT) and inactive dishes are never offered —
 * outlets never sell banquet dishes — except to a banquet's own Not-in-Menu dish
 * (allowBanquet), which is offered the banquet dishes first. A dish with no recipe is labelled, because
 * confirming it would move no stock (the server refuses it).
 *
 * Sales Name Check passes three optional props; Not in Menu passes none, so its
 * picker behaves exactly as before:
 *   preferIds  Set of menu ids listed first among the search results (the
 *              dishes with no sales in the checked month, spec §12);
 *   labelFor   (menuItemId) => string|null — an extra label on chips and results;
 *   autoFocus  focus the search box when the picker opens (keyboard L).
 */
const idOf = (m) => m.menu_item_id ?? m.id;
const isBqt = (m) => /_bqt$/i.test(String(m?.name || ''));
const isPickable = (m, allowBanquet) => m && m.is_active !== false && (allowBanquet || !isBqt(m));

export default function MenuItemPicker({
  menuItems, suggestions = [], disabled = false, onPick, preferIds = null, labelFor = null, autoFocus = false,
  allowBanquet = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const pickable = useMemo(() => (menuItems || []).filter((m) => isPickable(m, allowBanquet)), [menuItems, allowBanquet]);
  const ingredientCount = useMemo(
    () => new Map(pickable.map((m) => [Number(idOf(m)), Number(m.ingredient_count)])), [pickable]);
  const matches = useMemo(() => {
    if (!query.trim()) return [];
    if (allowBanquet) {
      const ranked = sortBySearchPriority(pickable, query, (m) => m.name, { limit: 40 });
      return [...ranked.filter(isBqt), ...ranked.filter((m) => !isBqt(m))].slice(0, 8);
    }
    if (!preferIds) return sortBySearchPriority(pickable, query, (m) => m.name, { limit: 8 });
    const ranked = sortBySearchPriority(pickable, query, (m) => m.name, { limit: 40 });
    const first = ranked.filter((m) => preferIds.has(Number(idOf(m))));
    const rest = ranked.filter((m) => !preferIds.has(Number(idOf(m))));
    return [...first, ...rest].slice(0, 8);
  }, [pickable, query, preferIds, allowBanquet]);
  const chips = useMemo(() => {
    const allowed = new Set(pickable.map((m) => Number(idOf(m))));
    return suggestions.filter((s) => pickable.length === 0 || allowed.has(Number(s.menu_item_id))).slice(0, 3);
  }, [suggestions, pickable]);

  const noRecipe = (id) => ingredientCount.get(Number(id)) === 0;
  const extraLabel = (id) => (labelFor ? labelFor(Number(id)) : null);
  const choose = (m) => {
    setQuery('');
    setOpen(false);
    setActiveIdx(0);
    onPick(m);
  };
  const onKeyDown = (e) => {
    if (matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[activeIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="nim-picker">
      {chips.length > 0 && (
        <div className="um-chips">
          {chips.map((s) => (
            <button key={s.menu_item_id} type="button" className="um-chip" disabled={disabled}
              onClick={() => choose({ menu_item_id: s.menu_item_id, name: s.name })}>
              {s.name}{s.price_exact ? ' · ₹ matches' : ''}
              {extraLabel(s.menu_item_id) ? ` · ${extraLabel(s.menu_item_id)}` : ''}
              {noRecipe(s.menu_item_id) ? ' · no recipe' : ''}
            </button>
          ))}
        </div>
      )}
      <div className="um-search-wrap">
        <input
          type="text" value={query} disabled={disabled} placeholder="Search menu items…" aria-label="Search menu items"
          autoFocus={autoFocus}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        />
        {open && matches.length > 0 && (
          <div className="um-dropdown" role="listbox">
            {matches.map((m, i) => (
              <div key={idOf(m)} role="option" aria-selected={i === activeIdx}
                className={`um-dropdown-item${i === activeIdx ? ' nim-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); choose(m); }}>
                {m.name}
                {m.price != null && <span className="um-muted"> · ₹{parseFloat(m.price).toFixed(0)}</span>}
                {extraLabel(idOf(m)) && <span className="um-muted"> · {extraLabel(idOf(m))}</span>}
                {noRecipe(idOf(m)) && <span className="nim-norecipe"> no recipe</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
